import React, { useEffect, useMemo, useState } from "react";

const EXPECTED_LENGTH = {
  Daily3: 3,
  Daily4: 4,
};

const STORAGE_PREFIX = "lotto_pick_v2";

const todayISO = () => new Date().toISOString().slice(0, 10);
const storageKey = (game, date) => `${STORAGE_PREFIX}:${game}:${date}`;

const extractDigits = (draw) => {
  const wn = draw?.WinningNumbers || {};
  return Object.keys(wn)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => String(wn[k]?.Number ?? "").trim())
    .filter(Boolean);
};

// Deterministic PRNG (mulberry32)
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const seedFromGameDate = (game, date) => {
  const str = `${game}:${date}`;
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const weightedPickWithRng = (weights, rng) => {
  const entries = Object.entries(weights);
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  let r = rng() * total;

  for (const [digit, weight] of entries) {
    r -= weight;
    if (r <= 0) return digit;
  }
  return entries[0]?.[0] ?? "0";
};

export default function AIGenerator({ game }) {
  const len = EXPECTED_LENGTH[game] ?? 3;

  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState([]);
  const [error, setError] = useState("");

  // ALWAYS show today's date (auto-updates at midnight)
  const [date, setDate] = useState(todayISO());

  // Update "date" at midnight without a page refresh
  useEffect(() => {
    const scheduleMidnightUpdate = () => {
      const now = new Date();
      const nextMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0,
        0,
        0,
        0
      );
      const msUntilMidnight = nextMidnight.getTime() - now.getTime();

      return setTimeout(() => {
        setDate(todayISO());
      }, msUntilMidnight);
    };

    const timer = scheduleMidnightUpdate();
    return () => clearTimeout(timer);
  }, []);

  // Fetch last draws via Vercel serverless function
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(`/api/draw?game=${game}`);

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || `API error: ${res.status}`);
        }

        const data = await res.json();

        const last10 = (data?.PreviousDraws || [])
          .slice(0, 10)
          .map((d) => extractDigits(d))
          .filter((d) => d.length === len);

        setDraws(last10);
      } catch (e) {
        console.error(e);
        setDraws([]);
        setError("Could not load recent draws.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [game, len]);

  // Build frequency weights by position from last 10 draws
  const weightsByPosition = useMemo(() => {
    const weights = Array.from({ length: len }, () => ({}));

    draws.forEach((digits) => {
      digits.forEach((digit, idx) => {
        weights[idx][digit] = (weights[idx][digit] || 0) + 1;
      });
    });

    // fallback to uniform if empty
    weights.forEach((w) => {
      if (Object.keys(w).length === 0) {
        for (let i = 0; i <= 9; i++) w[String(i)] = 1;
      }
    });

    return weights;
  }, [draws, len]);

  // Load locked pick for today's date if it exists
  useEffect(() => {
    const saved = localStorage.getItem(storageKey(game, date));
    if (saved) {
      try {
        setPick(JSON.parse(saved));
      } catch {
        setPick([]);
      }
    } else {
      setPick([]);
    }
  }, [game, date]);

  const generateLockedPickForToday = () => {
    // If already generated for today, keep it
    const existing = localStorage.getItem(storageKey(game, date));
    if (existing) {
      try {
        setPick(JSON.parse(existing));
        return;
      } catch {
        // if corrupted, continue to regenerate and overwrite
      }
    }

    const rng = mulberry32(seedFromGameDate(game, date));
    const result = weightsByPosition.map((w) => weightedPickWithRng(w, rng));

    localStorage.setItem(storageKey(game, date), JSON.stringify(result));
    setPick(result);
  };

  // Auto-generate on load (only once per day per game)
  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (draws.length === 0) return;

    const existing = localStorage.getItem(storageKey(game, date));
    if (!existing) {
      generateLockedPickForToday();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, draws, error, game, date]);

  return (
    <div className="generator">
      <h2>{game} Pick</h2>
      <div style={{ opacity: 0.8, marginBottom: 8 }}>For {date}</div>

      {loading && <p>Loading recent draws...</p>}
      {error && <p style={{ color: "crimson" }}>{error} Check /api/draws.</p>}

      {!loading && !error && pick.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div>
            <strong>Today’s pick:</strong> {pick.join("-")}
          </div>
        </div>
      )}
    </div>
  );
}

